import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Box,
  Button,
  Chip,
  LinearProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import { useTranslation } from "react-i18next";
import { getPlatformWorkspaceAnalytics } from "../../../api/client";

function StatCard({ title, value, subtitle }) {
  return (
    <Paper variant="outlined" sx={{ p: 2, height: "100%" }}>
      <Typography variant="caption" color="text.secondary" display="block">
        {title}
      </Typography>
      <Typography variant="h5" sx={{ mt: 0.5 }}>
        {value}
      </Typography>
      {subtitle ? (
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
          {subtitle}
        </Typography>
      ) : null}
    </Paper>
  );
}

const AnalyticsTabPanel = () => {
  const { t } = useTranslation("app");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await getPlatformWorkspaceAnalytics();
      setData(res);
    } catch (err) {
      setError(err?.message || t("platformPage.analyticsLoadError"));
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    load();
  }, [load]);

  const cohortMax = useMemo(() => {
    const rows = data?.cohorts_first_subscription || [];
    let m = 1;
    for (const r of rows) m = Math.max(m, Number(r.workspaces || 0));
    return m;
  }, [data]);

  const reasonLabel = useCallback(
    (code) =>
      t(`platformPage.analyticsReason_${code}`, {
        defaultValue: String(code || "").replace(/_/g, " "),
      }),
    [t]
  );

  const subscriptionStatusLabel = useCallback(
    (status) =>
      t(`billingPage.subscriptionStatus.${String(status || "").toLowerCase()}`, {
        defaultValue: String(status || "-"),
      }),
    [t]
  );

  const formatPct = (v) => (v == null ? "—" : `${v > 0 ? "+" : ""}${v}%`);

  if (loading && !data) {
    return (
      <Box sx={{ py: 3 }}>
        <LinearProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <Typography color="error" sx={{ mb: 2 }}>
          {error}
        </Typography>
        <Button startIcon={<RefreshIcon />} variant="outlined" size="small" onClick={load}>
          {t("billingPage.refresh")}
        </Button>
      </Box>
    );
  }

  const summary = data?.summary;
  const churn = data?.churn_signals;
  const usageCmp = data?.usage?.interactions_7d_vs_prev;
  const daily = data?.usage?.interactions_daily_56d || [];
  const cohorts = data?.cohorts_first_subscription || [];
  const atRisk = data?.at_risk_workspaces || [];

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 2 }} flexWrap="wrap" gap={1}>
        <Box sx={{ maxWidth: 920 }}>
          <Typography variant="body2" color="text.secondary">
            {t("platformPage.analyticsIntro")}
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.75 }}>
            {t("platformPage.analyticsScopeNote")}
          </Typography>
        </Box>
        <Button startIcon={<RefreshIcon />} variant="outlined" size="small" onClick={load} disabled={loading}>
          {t("billingPage.refresh")}
        </Button>
      </Stack>

      {loading ? <LinearProgress sx={{ mb: 2 }} /> : null}

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", md: "repeat(4, 1fr)" },
          gap: 2,
          mb: 3,
        }}
      >
        <StatCard title={t("platformPage.analyticsKpiTotal")} value={summary?.total_workspaces ?? "—"} />
        <StatCard
          title={t("platformPage.analyticsKpiExcluded")}
          value={summary?.excluded_workspaces ?? "—"}
          subtitle={t("platformPage.analyticsKpiExcludedHint")}
        />
        <StatCard
          title={t("platformPage.analyticsKpiWithSub")}
          value={summary?.with_subscription ?? "—"}
        />
        <StatCard
          title={t("platformPage.analyticsKpiNoContract")}
          value={summary?.without_subscription ?? "—"}
        />
      </Box>

      <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
        {t("platformPage.analyticsSectionStatus")}
      </Typography>
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 3 }}>
        <Chip label={`${t("billingPage.subscriptionStatus.trialing")}: ${summary?.by_status?.trialing ?? 0}`} size="small" />
        <Chip label={`${t("billingPage.subscriptionStatus.active")}: ${summary?.by_status?.active ?? 0}`} size="small" color="success" variant="outlined" />
        <Chip label={`${t("billingPage.subscriptionStatus.past_due")}: ${summary?.by_status?.past_due ?? 0}`} size="small" color="warning" variant="outlined" />
        <Chip label={`${t("billingPage.subscriptionStatus.suspended")}: ${summary?.by_status?.suspended ?? 0}`} size="small" color="error" variant="outlined" />
      </Stack>

      <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
        {t("platformPage.analyticsSectionChurn")}
      </Typography>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "repeat(2, 1fr)" },
          gap: 2,
          mb: 3,
        }}
      >
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Stack spacing={0.75}>
            <Typography variant="body2">
              <strong>{churn?.past_due ?? 0}</strong> {t("platformPage.analyticsChurnPastDue")}
            </Typography>
            <Typography variant="body2">
              <strong>{churn?.suspended ?? 0}</strong> {t("platformPage.analyticsChurnSuspended")}
            </Typography>
            <Typography variant="body2">
              <strong>{churn?.trialing_ending_7d ?? 0}</strong> {t("platformPage.analyticsChurnTrialEnd")}
            </Typography>
            <Typography variant="body2">
              <strong>{churn?.active_or_trialing_dormant_30d ?? 0}</strong> {t("platformPage.analyticsChurnDormant")}
            </Typography>
            <Typography variant="body2">
              <strong>{churn?.no_contract ?? 0}</strong> {t("platformPage.analyticsChurnNoContract")}
            </Typography>
          </Stack>
        </Paper>
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            {t("platformPage.analyticsSectionUsage")}
          </Typography>
          <Typography variant="body2" sx={{ mb: 0.5 }}>
            {t("platformPage.analyticsUsage7d")}: <strong>{usageCmp?.interactions_7d ?? 0}</strong>
          </Typography>
          <Typography variant="body2" sx={{ mb: 0.5 }}>
            {t("platformPage.analyticsUsagePrev7d")}: <strong>{usageCmp?.interactions_prev_7d ?? 0}</strong>
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t("platformPage.analyticsUsageWoW")}: {formatPct(usageCmp?.pct_change)}
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1.5 }}>
            {t("platformPage.analyticsDailyHint", { days: daily.length })}
          </Typography>
          {daily.length ? (
            <Box sx={{ mt: 1, display: "flex", alignItems: "flex-end", gap: 0.25, height: 56, overflowX: "auto" }}>
              {daily.map((row) => {
                const max = Math.max(...daily.map((d0) => Number(d0.interactions || 0)), 1);
                const h = Math.max(4, (Number(row.interactions || 0) / max) * 48);
                return (
                  <Box
                    key={row.date}
                    title={`${row.date}: ${row.interactions}`}
                    sx={{
                      width: 6,
                      minWidth: 6,
                      height: h,
                      bgcolor: "primary.main",
                      opacity: 0.75,
                      borderRadius: 0.5,
                    }}
                  />
                );
              })}
            </Box>
          ) : null}
        </Paper>
      </Box>

      <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
        {t("platformPage.analyticsSectionPlans")}
      </Typography>
      <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{t("platformPage.analyticsColPlan")}</TableCell>
              <TableCell align="right">{t("platformPage.analyticsColCount")}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(data?.by_plan || []).map((row) => (
              <TableRow key={`${row.plan_sku}-${row.plan_name}`}>
                <TableCell>
                  {row.plan_name}
                  {row.plan_sku ? (
                    <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                      ({row.plan_sku})
                    </Typography>
                  ) : null}
                </TableCell>
                <TableCell align="right">{row.count}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
        {t("platformPage.analyticsSectionBillingMode")}
      </Typography>
      <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{t("billingPage.adminFieldBillingMode")}</TableCell>
              <TableCell align="right">{t("platformPage.analyticsColCount")}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(data?.by_billing_mode || []).map((row) => {
              const m = row.billing_mode;
              const label = m ? t(`billingPage.billingMode.${m}`, { defaultValue: m }) : "—";
              return (
                <TableRow key={String(row.billing_mode)}>
                  <TableCell>{label}</TableCell>
                  <TableCell align="right">{row.count}</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
        {t("platformPage.analyticsSectionCohort")}
      </Typography>
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
        {t("platformPage.analyticsCohortHint")}
      </Typography>
      <Stack spacing={1} sx={{ mb: 3 }}>
        {cohorts.map((row) => (
          <Box key={row.month}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.25 }}>
              <Typography variant="body2">{row.month}</Typography>
              <Typography variant="body2">
                {row.workspaces} {t("platformPage.analyticsCohortWorkspaces")}
              </Typography>
            </Stack>
            <LinearProgress variant="determinate" value={(Number(row.workspaces || 0) / cohortMax) * 100} sx={{ height: 8, borderRadius: 1 }} />
          </Box>
        ))}
      </Stack>

      <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
        {t("platformPage.analyticsSectionAtRisk")}
      </Typography>
      {atRisk.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          {t("platformPage.analyticsEmptyAtRisk")}
        </Typography>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t("platformPage.colDominio")}</TableCell>
                <TableCell>{t("platformPage.colSubscriptionStatus")}</TableCell>
                <TableCell>{t("platformPage.analyticsAtRiskReason")}</TableCell>
                <TableCell align="right">{t("platformPage.colUsageIx30d")}</TableCell>
                <TableCell align="right">{t("platformPage.colActions")}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {atRisk.map((row) => (
                <TableRow key={row.id}>
                  <TableCell>
                    <Typography variant="body2">{row.dominio}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {row.bd}
                    </Typography>
                  </TableCell>
                  <TableCell>{subscriptionStatusLabel(row.subscription_status)}</TableCell>
                  <TableCell>
                    <Stack direction="row" flexWrap="wrap" gap={0.5}>
                      {(row.churn_reasons || []).map((c) => (
                        <Chip key={c} label={reasonLabel(c)} size="small" variant="outlined" />
                      ))}
                    </Stack>
                  </TableCell>
                  <TableCell align="right">{row.usage_interactions_30d}</TableCell>
                  <TableCell align="right">
                    <Button component={RouterLink} to={`workspaces/${row.id}`} size="small">
                      {t("platformPage.openWorkspace")}
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {data?.generated_at ? (
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 2 }}>
          {new Date(data.generated_at).toLocaleString()}
        </Typography>
      ) : null}
    </Box>
  );
};

export default AnalyticsTabPanel;
